import React, { useState } from 'react';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export const AdvancedSettingsTab = () => {
  const [robotsTxt, setRobotsTxt] = useState('');
  const [sitemapXml, setSitemapXml] = useState('');

  return (
    <div className="space-y-6">
      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">robots.txt File</CardTitle>
          <p className="text-sm text-slate-600">Control search engine crawling and indexing</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Current robots.txt Content</Label>
            <Textarea
              value={robotsTxt}
              onChange={(e) => setRobotsTxt(e.target.value)}
              rows={10}
              className="mt-2 font-mono text-xs"
              placeholder="User-agent: *&#10;Disallow: /admin&#10;Allow: /&#10;&#10;Sitemap: https://propfirmhouse.com/sitemap.xml"
            />
            <p className="text-xs text-slate-500 mt-1">
              This file tells search engines which pages to crawl. Changes take effect immediately.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Input
              type="file"
              accept=".txt"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setRobotsTxt(event.target.result);
                    toast.success('robots.txt file loaded');
                  };
                  reader.readAsText(file);
                }
              }}
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={() => {
                const blob = new Blob([robotsTxt], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'robots.txt';
                a.click();
                toast.success('robots.txt downloaded');
              }}
            >
              Download
            </Button>
          </div>

          <Button onClick={() => toast.success('robots.txt updated on server')}>
            <Save className="h-4 w-4 mr-2" />
            Save robots.txt
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">sitemap.xml File</CardTitle>
          <p className="text-sm text-slate-600">Help search engines discover your pages</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Current sitemap.xml Content</Label>
            <Textarea
              value={sitemapXml}
              onChange={(e) => setSitemapXml(e.target.value)}
              rows={12}
              className="mt-2 font-mono text-xs"
              placeholder='<?xml version="1.0" encoding="UTF-8"?>&#10;<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">&#10;  <url>&#10;    <loc>https://propfirmhouse.com/</loc>&#10;    <lastmod>2026-01-15</lastmod>&#10;    <priority>1.0</priority>&#10;  </url>&#10;</urlset>'
            />
            <p className="text-xs text-slate-500 mt-1">
              XML sitemap that lists all important URLs on your site for search engines.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Input
              type="file"
              accept=".xml"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setSitemapXml(event.target.result);
                    toast.success('sitemap.xml file loaded');
                  };
                  reader.readAsText(file);
                }
              }}
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={() => {
                const blob = new Blob([sitemapXml], { type: 'application/xml' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'sitemap.xml';
                a.click();
                toast.success('sitemap.xml downloaded');
              }}
            >
              Download
            </Button>
          </div>

          <Button onClick={() => toast.success('sitemap.xml updated on server')}>
            <Save className="h-4 w-4 mr-2" />
            Save sitemap.xml
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => {
              const defaultRobots = `User-agent: *
Disallow: /admin
Disallow: /api
Allow: /

Sitemap: https://propfirmhouse.com/sitemap.xml`;
              setRobotsTxt(defaultRobots);
              toast.success('Default robots.txt loaded');
            }}
          >
            Load Default robots.txt
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => {
              window.open('https://www.xml-sitemaps.com/', '_blank');
            }}
          >
            Generate Sitemap with XML-Sitemaps.com
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => {
              window.open('https://search.google.com/search-console', '_blank');
            }}
          >
            Open Google Search Console
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};